// src/components/ImageDropzone.js
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FiCloudLightning, FiPlay, FiX } from "react-icons/fi";

const ImageDropzone = ({
  files,
  setFiles,
  maxFiles = 1,
  allowVideo = false,
}) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      const filesWithPreview = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          mediaType: file.type.startsWith("video/") ? "video" : "image",
        })
      );

      if (maxFiles === 1) {
        setFiles(filesWithPreview);
      } else {
        setFiles((prevFiles) => {
          const newFiles = filesWithPreview.filter(
            (newFile) =>
              !prevFiles.some(
                (prevFile) =>
                  prevFile.name === newFile.name &&
                  prevFile.size === newFile.size
              )
          );
          return [...prevFiles, ...newFiles];
        });
      }
    },
    [setFiles, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      ...(allowVideo ? { "video/*": [] } : {}),
    },
    maxFiles: maxFiles,
  });

  const removeFile = (fileName, e) => {
    e.stopPropagation();
    setFiles(files.filter((file) => file.name !== fileName));
  };

  const previews = files.map((file) => (
    <div key={file.name} className="relative group p-1 shrink-0">
      <div className="w-24 h-24 rounded-2xl overflow-hidden border border-component-border shadow-sm bg-white">
        {file.mediaType === "image" ? (
          <img
            src={file.preview}
            className="w-full h-full object-cover"
            alt="Preview"
            onLoad={() => URL.revokeObjectURL(file.preview)}
          />
        ) : (
          <div className="relative w-full h-full bg-theme-text">
            <video
              src={file.preview}
              className="w-full h-full object-cover opacity-80"
              muted
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-9 h-9 rounded-full bg-white/90 text-theme-text flex items-center justify-center shadow-lg">
                <FiPlay size={16} />
              </div>
            </div>
          </div>
        )}
      </div>
      <button
        onClick={(e) => removeFile(file.name, e)}
        className="absolute -top-1 -right-1 w-7 h-7 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-90 transition-all z-10"
      >
        <FiX size={14} />
      </button>
      {file.size > 0 && (
         <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-theme-text text-white text-[8px] font-black rounded-full uppercase tracking-tighter whitespace-nowrap">
           {(file.size / 1024 / 1024).toFixed(1)}MB
         </div>
      )}
    </div>
  ));

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`relative group cursor-pointer border-4 border-dashed rounded-[32px] p-10 transition-all duration-300 ${
          isDragActive
            ? "border-chafadi-primary bg-chafadi-primary/5 scale-[0.98]"
            : "border-gray-100 bg-theme-bg hover:border-gray-200 hover:bg-white"
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center text-center space-y-4">
          <div className={`w-20 h-20 rounded-[32px] flex items-center justify-center text-4xl transition-all duration-500 ${
            isDragActive ? "bg-chafadi-primary text-white rotate-12" : "bg-white text-gray-300 shadow-sm"
          }`}>
            <FiCloudLightning />
          </div>
          <div className="space-y-1">
            <p className="font-black text-theme-text uppercase tracking-widest text-sm">
              {isDragActive ? "Release to sync" : "Select Assets"}
            </p>
            <p className="text-theme-muted font-medium text-xs leading-relaxed max-w-[200px]">
              Drag {allowVideo ? "images or videos" : "images"} here or click to browse files
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white/50 backdrop-blur-md px-4 py-1.5 rounded-full border border-component-border">
            <span className="w-1.5 h-1.5 bg-chafadi-primary rounded-full animate-pulse" />
            <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest">
              Upto {maxFiles} {maxFiles > 1 ? "Files" : "File"}
            </span>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 animate-fade-in p-4 bg-gray-50/50 rounded-[32px] border border-gray-100">
          {previews}
          {files.length < maxFiles && (
            <div 
              {...getRootProps()} 
              className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-300 hover:border-chafadi-primary hover:text-chafadi-primary transition-all cursor-pointer bg-white"
            >
              <FiCloudLightning className="mb-1" />
              <span className="text-[10px] font-black uppercase tracking-tighter">Add More</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
